import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Puzzle, Save } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { usePermission } from '../../hooks/usePermission';
import { apiClient } from '../../lib/api-client';

type PluginRow = {
  id: string;
  label: string;
  description?: string;
  source: 'native' | 'custom_private';
  enabled: boolean;
  capabilities?: Array<{ id: string; label: string; description?: string }>;
  bundleRef?: string;
};

const blankForm: PluginRow = {
  id: '',
  label: '',
  description: '',
  source: 'custom_private',
  enabled: true,
  capabilities: [],
  bundleRef: '',
};

function normalizeCapabilities(input: unknown): Array<{ id: string; label: string; description?: string }> {
  if (!Array.isArray(input)) return [];
  return input
    .map((item: any) => {
      if (typeof item === 'string') {
        const id = item.trim();
        return id ? { id, label: id } : null;
      }
      const id = String(item?.id || '').trim();
      if (!id) return null;
      return {
        id,
        label: String(item?.label || id),
        description: item?.description ? String(item.description) : undefined,
      };
    })
    .filter(Boolean) as Array<{ id: string; label: string; description?: string }>;
}

function capabilitiesToText(capabilities: unknown): string {
  return normalizeCapabilities(capabilities).map((capability) => capability.id).join(', ');
}

function textToCapabilities(text: string): Array<{ id: string; label: string }> {
  return text.split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((id) => ({ id, label: id }));
}

export function Plugins() {
  const { isAllowed: canRead } = usePermission('plugins', 'read');
  const { isAllowed: canManage } = usePermission('plugins', 'manage');
  const [rows, setRows] = useState<PluginRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<PluginRow>(blankForm);

  const loadRows = useCallback(async () => {
    if (!canRead) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.getAiPlugins();
      setRows((Array.isArray(response.plugins) ? response.plugins : []).map((row: PluginRow) => ({
        ...row,
        capabilities: normalizeCapabilities(row.capabilities),
      })));
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load plugins');
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => `${row.id} ${row.label} ${row.description || ''}`.toLowerCase().includes(query));
  }, [rows, search]);

  const openEditor = (row?: PluginRow) => {
    setForm(row ? {
      ...row,
      capabilities: normalizeCapabilities(row.capabilities),
    } : blankForm);
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.id.trim()) return;
    setSaving(true);
    try {
      await apiClient.upsertAiPlugin(form.id.trim(), {
        ...form,
        capabilities: normalizeCapabilities(form.capabilities),
      });
      setEditOpen(false);
      await loadRows();
    } catch (err: any) {
      setError(err?.message || 'Failed to save plugin');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Puzzle}
        title="Plugins"
        description="Manage Cursor plugin bundles that generated agent repositories can reference. Seed bundles are built-in; custom bundles can point to private package or repository refs."
        count={filtered.length}
        countLabel="plugins"
        actions={(
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search plugins..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              wrapperClassName="w-64"
            />
            {canManage ? (
              <Button onClick={() => openEditor()}>
                <Plus className="mr-2 h-4 w-4" />
                Add Plugin
              </Button>
            ) : null}
          </div>
        )}
      />

      {!canRead ? (
        <Card><CardContent className="py-10 text-sm text-gray-500 dark:text-gray-400">No access.</CardContent></Card>
      ) : loading ? (
        <Card><CardContent className="py-10 text-sm text-gray-500 dark:text-gray-400">Loading plugins...</CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((row) => (
            <Card key={row.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.id}</p>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{row.label || row.id}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{row.description || 'No description'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant={row.enabled ? 'success' : 'secondary'}>{row.enabled ? 'enabled' : 'disabled'}</Badge>
                    <Badge variant="outline">{row.source}</Badge>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {row.bundleRef || 'No bundle ref configured'}
                </div>
                {row.capabilities?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {row.capabilities.slice(0, 4).map((capability) => (
                      <Badge key={capability.id} variant="secondary">{capability.id}</Badge>
                    ))}
                    {row.capabilities.length > 4 ? <Badge variant="outline">+{row.capabilities.length - 4}</Badge> : null}
                  </div>
                ) : null}
                {canManage ? (
                  <Button variant="outline" className="w-full" onClick={() => openEditor(row)}>Edit</Button>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {error ? (
        <Card><CardContent className="py-3 text-sm text-red-600 dark:text-red-400">{error}</CardContent></Card>
      ) : null}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent maxWidth="4xl" className="max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit Plugin' : 'Create Plugin'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-5 overflow-y-auto">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100">
              Plugins are generated into `.cursor/plugins/manifest.json`. Use built-in seed rows for MaxIron/Cursor bundles, or create a custom private plugin by entering the package, registry, or repository bundle reference supported by Cursor.
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Plugin id</span>
                <Input
                  placeholder="cursor-docs"
                  value={form.id}
                  onChange={(event) => setForm((prev) => ({ ...prev, id: event.target.value }))}
                  disabled={Boolean(form.id && rows.some((row) => row.id === form.id))}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Label</span>
                <Input
                  placeholder="Cursor Docs"
                  value={form.label}
                  onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="font-medium text-gray-700 dark:text-gray-200">Description</span>
                <Input
                  placeholder="What this bundle adds to generated Cursor repositories"
                  value={form.description || ''}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-200">Source</span>
                <select
                  value={form.source}
                  onChange={(event) => setForm((prev) => ({ ...prev, source: event.target.value as 'native' | 'custom_private' }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="custom_private">custom_private</option>
                  <option value="native">native</option>
                </select>
              </label>
              <label className="flex items-end gap-2 pb-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                />
                enabled
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="font-medium text-gray-700 dark:text-gray-200">Capabilities</span>
                <Input
                  placeholder="rules, skills, hooks, mcp"
                  value={capabilitiesToText(form.capabilities)}
                  onChange={(event) => setForm((prev) => ({
                    ...prev,
                    capabilities: textToCapabilities(event.target.value),
                  }))}
                />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="font-medium text-gray-700 dark:text-gray-200">Bundle reference</span>
                <Input
                  placeholder="@cursor/plugin-name or github-org/repo/path"
                  value={form.bundleRef || ''}
                  onChange={(event) => setForm((prev) => ({ ...prev, bundleRef: event.target.value }))}
                />
              </label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={() => void save()} disabled={saving || !form.id.trim()}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

